import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { notificationApi } from '../services/notificationApi';
import { useAuth } from '../context/AuthContext';

export interface Notification {
  id: string;
  type: 'message' | 'application' | 'payment' | 'maintenance' | 'reminder' | 'system';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl?: string;
  actionLabel?: string;
  relatedId?: string; // Property ID, Message ID, etc.
  icon?: string;
  avatar?: string;
  metadata?: {
    propertyTitle?: string;
    senderName?: string;
    amount?: number;
    dueDate?: string;
    propertyAddress?: string;
    applicationStatus?: string;
  };
}

export interface NotificationPreferences {
  email: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
    marketing: boolean;
  };
  push: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
  };
  inApp: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isLoading: boolean;
  error: string | null;
}

type NotificationAction =
  | { type: 'SET_NOTIFICATIONS'; payload: Notification[] }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_AS_READ'; payload: string[] }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_PREFERENCES'; payload: NotificationPreferences }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

const defaultPreferences: NotificationPreferences = {
  email: {
    messages: true,
    applications: true,
    payments: true,
    maintenance: true,
    reminders: true,
    marketing: false,
  },
  push: {
    messages: true,
    applications: true,
    payments: true,
    maintenance: true,
    reminders: true,
  },
  inApp: {
    messages: true,
    applications: true,
    payments: true,
    maintenance: true,
    reminders: true,
  },
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  preferences: defaultPreferences,
  isLoading: false,
  error: null,
};

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.isRead).length
      };
    
    case 'ADD_NOTIFICATION':
      const newNotifications = [action.payload, ...state.notifications];
      return {
        ...state,
        notifications: newNotifications,
        unreadCount: newNotifications.filter(n => !n.isRead).length
      };
    
    case 'MARK_AS_READ':
      const updatedNotifications = state.notifications.map(notification =>
        action.payload.includes(notification.id)
          ? { ...notification, isRead: true }
          : notification
      );
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter(n => !n.isRead).length
      };
    
    case 'DELETE_NOTIFICATION':
      const filteredNotifications = state.notifications.filter(n => n.id !== action.payload);
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter(n => !n.isRead).length
      };
    
    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: [],
        unreadCount: 0
      };
    
    case 'SET_PREFERENCES':
      return { ...state, preferences: action.payload };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    default:
      return state;
  }
};

const NotificationContext = createContext<{
  state: NotificationState;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  markAsRead: (notificationIds: string[]) => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  updatePreferences: (preferences: NotificationPreferences) => void;
  requestPermission: () => Promise<boolean>;
  showBrowserNotification: (notification: Notification) => void;
} | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { token } = useAuth();

  // Load notifications from the API whenever auth state changes. This provider is
  // mounted once at the app root, so a mount-only effect only sees whatever token
  // exists at that instant — if the user logs in during the same SPA session
  // (no full page reload), the token appears in localStorage afterward and the
  // one-time effect never re-runs, leaving the notification list permanently
  // empty. Depending on AuthContext's reactive `token` fixes that.
  useEffect(() => {
    if (!token) {
      console.log('🔒 No token found, skipping notification fetch');
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] });
      return;
    }

    const fetchNotifications = async () => {
      try {
        console.log('🔔 Fetching notifications from API...');
        dispatch({ type: 'SET_LOADING', payload: true });
        const response = await notificationApi.getNotifications();

        if (response.success) {
          console.log('✅ Notifications loaded successfully:', response.data.notifications.length);
          dispatch({ type: 'SET_NOTIFICATIONS', payload: response.data.notifications });
        } else {
          console.warn('⚠️ API returned success: false for notifications');
        }
      } catch (error) {
        console.error('❌ Error loading notifications:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load notifications' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    fetchNotifications();
  }, [token]);

  // Load preferences from the API whenever auth state changes (same reasoning as above).
  useEffect(() => {
    if (!token) {
      // User is not authenticated, load from localStorage only
      const savedPreferences = localStorage.getItem('notificationPreferences');
      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          dispatch({ type: 'SET_PREFERENCES', payload: preferences });
        } catch (error) {
          console.error('Error loading notification preferences from localStorage:', error);
        }
      }
      return;
    }

    const fetchPreferences = async () => {
      try {
        const response = await notificationApi.getPreferences();
        if (response.success) {
          dispatch({ type: 'SET_PREFERENCES', payload: response.data });
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        // Fall back to localStorage if API fails
        const savedPreferences = localStorage.getItem('notificationPreferences');
        if (savedPreferences) {
          try {
            const preferences = JSON.parse(savedPreferences);
            dispatch({ type: 'SET_PREFERENCES', payload: preferences });
          } catch (error) {
            console.error('Error loading notification preferences from localStorage:', error);
          }
        }
      }
    };

    fetchPreferences();
  }, [token]);

  const addNotification = (notificationData: Omit<Notification, 'id' | 'timestamp'>) => {
    const notification: Notification = {
      ...notificationData,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    
    // Show browser notification if enabled
    if (state.preferences.push[notification.type as keyof typeof state.preferences.push]) {
      showBrowserNotification(notification);
    }
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      // Update local state immediately for better UX
      dispatch({ type: 'MARK_AS_READ', payload: notificationIds });
      
      // Send to API
      await notificationApi.markAsRead(notificationIds);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      // Revert local state on error
      // You might want to implement a more sophisticated error handling here
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      // Update local state immediately for better UX
      dispatch({ type: 'DELETE_NOTIFICATION', payload: notificationId });
      
      // Send to API
      await notificationApi.deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Revert local state on error
      // You might want to implement a more sophisticated error handling here
    }
  };

  const clearAllNotifications = () => {
    dispatch({ type: 'CLEAR_ALL' });
  };

  const updatePreferences = async (preferences: NotificationPreferences) => {
    try {
      dispatch({ type: 'SET_PREFERENCES', payload: preferences });
      
      // Save to API
      await notificationApi.updatePreferences(preferences);
      
      // Also save to localStorage as backup
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      // Save to localStorage as fallback
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  };

  const showBrowserNotification = (notification: Notification) => {
    if (Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: notification.id,
        data: {
          url: notification.actionUrl
        }
      });

      browserNotification.onclick = () => {
        window.focus();
        if (notification.actionUrl) {
          window.location.href = notification.actionUrl;
        }
        browserNotification.close();
      };

      // Auto close after 5 seconds
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }
  };

  return (
    <NotificationContext.Provider value={{
      state,
      addNotification,
      markAsRead,
      deleteNotification,
      clearAllNotifications,
      updatePreferences,
      requestPermission,
      showBrowserNotification
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 